import classnames from "classnames";
import * as _ from "lodash";
import * as React from "react";
import { MenuItem, Nav, Navbar, NavDropdown, NavItem } from "react-bootstrap";
import { Permissions } from "../shared/model/AllPermissions";
import { HashMap } from "../shared/model/HashMap";
import { Utilities } from "../shared/utilities";

import { getLastReadNotification, getNotificationMessages } from "./actions/AppActions";
import NotificationCenter from "./components/NotificationCenter";
import { initialState, reducer } from "./reducers/AppReducer";
import { IAppReducerState } from "./shared/model";

export const releaseNoteStorageKey = "notification/releaseNote";
export const notificationStorageKey = "notification/lastMessages";
export const AppContext = React.createContext<IAppReducerState>({ appState: initialState, dispatch: () => null });

const App: React.FC<INavigationProps> = (props) => {

    const [appState, dispatch] = React.useReducer(reducer, initialState);

    const getUriPartsWithoutBase = (): string[] => {
        const urlPart = new URL(document.URL).pathname;
        const basePart = new URL(document.baseURI).pathname;
        const urlPartWithoutBase = urlPart.replace(basePart, "").split("/"); // Remove base from the url
        return urlPartWithoutBase;
    };

    const getEnvironmentFromUrl = (activeLink: LinkNames): AgentGroupEnvironment => {
        const baseUrlParts = getUriPartsWithoutBase();
        let environmentPart = "";
        let agentGroupPart = "";
        switch (activeLink) {
            case LinkNames.process: // Controller[0]/List[1]/environment[2]/agentGroup[3]
            case LinkNames.subprocess:
                if ((baseUrlParts[1] === "List") && baseUrlParts.length >= 3) { // Check if second url part is List to not change environment when navigating from process editor. Check that urlparts contain the environment
                    environmentPart = "/" + baseUrlParts[2];
                    agentGroupPart = baseUrlParts.length >= 4 ? "/" + baseUrlParts[3] : "";
                }
                break;
            case LinkNames.environmentvariables: // Controller[0]/List[1]/environment[2]
            case LinkNames.monitoringrules:
                if (baseUrlParts.length >= 3) {
                    environmentPart = "/" + baseUrlParts[2];
                }
                break;
            case LinkNames.environment: // Controller[0]/environment[1]/agentGroup[2]
            case LinkNames.apimanagement:
                if (baseUrlParts.length >= 2) {
                    environmentPart = "/" + baseUrlParts[1];
                    agentGroupPart = baseUrlParts.length >= 3 ? "/" + baseUrlParts[2] : "";
                }
                break;
            default:
                environmentPart = "";
        }
        return { agentGroupName: agentGroupPart, environmentName: environmentPart };
    };

    const urlParts = getUriPartsWithoutBase();
    const controller = urlParts[0];
    const userRights = Utilities.getAllUserPermissions();

    const getInitialState = () => {
        return {
            base: new URL(document.baseURI).pathname,
            currentAgentGroupEnvironment: getEnvironmentFromUrl((LinkNames as unknown as HashMap<LinkNames>)[controller.toLowerCase()]),
            hasRightsFor: userRights,
            activeLink: (LinkNames as unknown as HashMap<LinkNames>)[controller.toLowerCase()]
        };
    };

    const [state, setState] = React.useState<INavigationState>(getInitialState());

    React.useEffect(() => {
        getNotificationMessages(dispatch, notificationStorageKey);
        getLastReadNotification(dispatch, releaseNoteStorageKey);
        const tick = setInterval(() => {
            const env = getEnvironmentFromUrl((LinkNames as unknown as HashMap<LinkNames>)[controller.toLowerCase()]);
            if (!_.isEqual(env, state.currentAgentGroupEnvironment)) {
                setState({
                    ...state,
                    currentAgentGroupEnvironment: env
                });
            }
        }, 500);
        return () => clearInterval(tick);
    }, [state.currentAgentGroupEnvironment]);

    const getAuthenticationItems = () => {
        if (props.isAuthenticated === "True") {
            if (props.isSigninsupported === "True") {
                return (
                    <NavDropdown
                        pullRight={true}
                        id="signout-dropdown"
                        title={props.username}
                    >
                        <MenuItem href={state.base + "Account/SignOut"}>
                            {FRENDS.Localizations.SignOut}
                        </MenuItem>
                    </NavDropdown>
                );
            } else {
                return (
                    <li role="presentation">
                        <p className="navbar-text"><span className="glyphicon glyphicon-user"></span> {props.username}</p>
                    </li>
                );
            }
        } else if (props.isSigninsupported === "True") {
            return (
                <NavItem href={state.base + "Account/SignIn"} >
                    {FRENDS.Localizations.SignIn}
                </NavItem>
            );
        }
    };

    const getDropdownMenu = (link: INavigationBarData, linkIndex: number) => {
        const menuItems = link.children?.map(
            (childLink, childLinkIndex) => {
                if (state.hasRightsFor[childLink.permission]) {
                    const linkId = `nav_link_${linkIndex}_${childLinkIndex}`;
                    return <MenuItem
                        id={linkId}
                        key={linkId}
                        className={classnames("dropdown-menu-item menu-item-reversed-padding", { selected: (state.activeLink === childLink.activeLinkName) })}
                        href={generateHref(childLink)}
                    >
                        {childLink.label}
                    </MenuItem>;
                } else {
                    return null;
                }
            });

        return <NavDropdown
            id={"nav_link_" + linkIndex}
            key={"nav_link_" + linkIndex}
            eventKey={linkIndex}
            title={link.label}
            className={classnames({ active: (link.children?.some((x) => x.activeLinkName === state.activeLink) as boolean) })}
        >
            {menuItems}
        </NavDropdown>;
    };

    const generateHref = (link: INavigationBarData) => {
        if (!link.overwriteCurrentEnvironmentInUrl) {
            return state.base + link.href;
        }
        const environment = getEnvironmentFromUrl((LinkNames as unknown as HashMap<LinkNames>)[controller.toLowerCase()]); // Generation of the last '/' before environment is better to do in the getEnvironmentFromUrl() as if there is no environment no slash should be generated for react routing to work properly
        return state.base + link.href + environment.environmentName + (link.useAgentGroups ? environment.agentGroupName : "");
    };

    return (
        <AppContext.Provider value={{ appState, dispatch }}>
            <Navbar className="navbar-static-top">
                <Navbar.Header>
                    <Navbar.Brand>
                        <a href={state.base + "Dashboard"}>
                            <img src={state.base + "Content/Images/frends_logo.svg"} width="150" height="32" />
                        </a>
                    </Navbar.Brand>
                    <Navbar.Toggle />
                </Navbar.Header>
                <Navbar.Collapse>
                    <Nav id="menu">
                        {navigationBarData.map((link, linkIndex) => (
                            state.hasRightsFor[link.permission] ? (
                                link.children != null
                                    ? getDropdownMenu(link, linkIndex)
                                    : <NavItem
                                        href={generateHref(link)}
                                        className={classnames({ active: (state.activeLink === link.activeLinkName) })}
                                        key={link.activeLinkName}
                                    >
                                        {link.label}
                                    </NavItem>
                            ) : null
                        ))}
                    </Nav>
                    <ul className="nav navbar-nav navbar-right">
                        <NotificationCenter />
                        <Intercom intercomAppId={props.intercomAppId} />
                        {getAuthenticationItems()}
                    </ul>
                </Navbar.Collapse>
            </Navbar>
        </AppContext.Provider>
    );
};

const Intercom: React.FC<{ intercomAppId: string }> = ({ intercomAppId }) => {
    if (intercomAppId === "") {
        return (
            <NavItem target="_blank" href="https://docs.frends.com/">
                {FRENDS.Localizations.Help}
            </NavItem>
        );
    } else {
        return (
            <NavItem
                id="intercom_chat_launcher"
                href={`mailto:${intercomAppId}@hiq-finland-oy-4347f501d756.intercom-mail.com`}
            >
                <i className="fa fa-comment" aria-hidden="true"></i> {FRENDS.Localizations.Help}
            </NavItem>
        );
    }
};

// Make sure the links are in lowercase
// If you do not make them lowercase they will not be matched and the UI will not show the selected item
enum LinkNames {
    "apimanagement",
    "process",
    "subprocess",
    "environmentvariables",
    "administration",
    "environment",
    "task",
    "monitoringrules",
    "usermanagement",
    "apikeymanagement",
    "apiaccesspolicymanagement",
    "settings"
}
// Please look at the comment above the enum declaration if you have added any links here. Thanks!

const navigationBarData: INavigationBarData[] = [
    { href: "ApiManagement", activeLinkName: LinkNames.apimanagement, label: FRENDS.Localizations.ApiManagement, permission: Permissions.ApiManagementView, overwriteCurrentEnvironmentInUrl: true, useAgentGroups: true },
    { href: "Process/List", activeLinkName: LinkNames.process, label: FRENDS.Localizations.Processes, permission: Permissions.ProcessView, overwriteCurrentEnvironmentInUrl: true, useAgentGroups: true },
    { href: "SubProcess/List", activeLinkName: LinkNames.subprocess, label: FRENDS.Localizations.Subprocesses, permission: Permissions.SubProcessEdit, overwriteCurrentEnvironmentInUrl: true, useAgentGroups: true },
    { href: "EnvironmentVariables/List", activeLinkName: LinkNames.environmentvariables, label: FRENDS.Localizations.EnvironmentVariables, permission: Permissions.EnvironmentVariablesEdit, overwriteCurrentEnvironmentInUrl: true },
    {
        href: "", activeLinkName: LinkNames.administration, label: FRENDS.Localizations.Administration, permission: Permissions.AdministrationView, overwriteCurrentEnvironmentInUrl: false,
        children: [
            { href: "Environment", activeLinkName: LinkNames.environment, label: FRENDS.Localizations.Environments, permission: Permissions.EnvironmentEdit, overwriteCurrentEnvironmentInUrl: true, useAgentGroups: true },
            { href: "Task", activeLinkName: LinkNames.task, label: FRENDS.Localizations.Tasks, permission: Permissions.TaskView, overwriteCurrentEnvironmentInUrl: false },
            { href: "MonitoringRules/List", activeLinkName: LinkNames.monitoringrules, label: FRENDS.Localizations.MonitoringRules, permission: Permissions.MonitoringRulesView, overwriteCurrentEnvironmentInUrl: true },
            { href: "UserManagement", activeLinkName: LinkNames.usermanagement, label: FRENDS.Localizations.UserManagement, permission: Permissions.UserManagementAdmin, overwriteCurrentEnvironmentInUrl: false },
            { href: "ApiKeyManagement", activeLinkName: LinkNames.apikeymanagement, label: FRENDS.Localizations.Administration_ApiKeys, permission: Permissions.ApiKeyManagementAdmin, overwriteCurrentEnvironmentInUrl: false },
            { href: "ApiAccessPolicyManagement", activeLinkName: LinkNames.apiaccesspolicymanagement, label: FRENDS.Localizations.ApiAccessPolicies, permission: Permissions.ApiAccessPolicyManagementAdmin, overwriteCurrentEnvironmentInUrl: false },
            { href: "Settings", activeLinkName: LinkNames.settings, label: FRENDS.Localizations.Settings, permission: Permissions.SettingsAdmin, overwriteCurrentEnvironmentInUrl: false }
        ]
    }
];

export default App;

interface INavigationState {
    base: string;
    currentAgentGroupEnvironment: AgentGroupEnvironment;
    activeLink?: LinkNames;
    hasRightsFor: { [key: string]: boolean };
}

export interface INavigationProps {
    isAuthenticated: "True" | "False";
    isSigninsupported: "True" | "False";
    username: string;
    intercomAppId: string;
}

interface INavigationBarData {
    href: string;
    activeLinkName: LinkNames;
    permission: string;
    label: string;
    overwriteCurrentEnvironmentInUrl: boolean;
    useAgentGroups?: boolean;
    children?: INavigationBarData[];
}
interface AgentGroupEnvironment {
    environmentName: string;
    agentGroupName: string;
}
